/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 添加批量编辑照片页面
 * @LastEditors: yingongjing
 * @LastEditTime: 2026-05-19 09:56:22
 */
import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import { Layout, message, Button, Popover } from 'antd'
import { useNavigate } from 'react-router-dom'
import {
  CloseOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  LeftOutlined
} from '@ant-design/icons'
import PageHeader from '@/components/PageHeader'
import { useAppDispatch, useAppSelector } from '../../../../app/hooks'
import {
  setFilter,
  setQueryFilter,
  fetchPhotos,
  resetFilter,
  setLastPageCond
} from '../../../../actions/photo'
import {
  appendPhotoPickerPhotos,
  setPhotoPickerPhotos
} from '../../../../actions/photoPicker'
import { reset as resetPhotosByLocation } from '../../../../slices/photosByLocationSlice'
import { reset as resetPhotosByPrecise } from '../../../../slices/photosByPreciseSlice'
import { reset as resetAllPhotosSlice } from '../../../../slices/allPhotoSlice'
import {
  buildFetchPhotosParams,
  buildFetchPhotosParamsWithOptions
} from '@/pages/components/photoLib/photo'
import { capturePromiseException } from '../../../../utils/sentryUtils'
import actionTypes from '../../../../constants/actionTypes'
import { setLoading } from '../../../../actions/main'
import { setIsAscSort as setContentIsAscSort } from '../../../../slices/photosByContentSlice'
import { setIsAscSort as setPreciseIsAscSort } from '../../../../slices/photosByPreciseSlice'

import Filter from './components/Filter'
import HeaderExtra from './components/HeaderExtra'
import PhotosList from './components/PhotosList'
import { useAddPhotosTrack } from './hooks/useAddPhotosTrack'
import { formatDate } from './hooks/useAddPhotosTrack'
import './index.scss'
import type { PhotoItem } from '../../../watermark/batchEditor/context/WatermarkBatchContext'
// React Context 让子组件读到 aiClerk 注入的"排除已选"列表 + "放宽过滤"开关
import { TeamPhotoPickerContext } from './context'

const { Content } = Layout

interface AddBatchEditPhotosProps {
  inModal?: boolean
  onClose?: () => void
  currentPhoto?: PhotoItem | null
  // ─── aiClerk 调用方扩展点 ───────────────────────────────────────────
  /** 顶部标题(默认"添加批量编辑照片",aiClerk 传"团队相册选图") */
  headerTitle?: string
  /**
   * 完成回调:传了就走 callback,不再 dispatch 到 photoPicker / push 路由。
   * batch-editor 维持默认行为(不传 → 老链路)
   */
  onConfirm?: (photos: any[]) => void
  /** 排除已选(seq / smallURL 任一命中即过滤掉) */
  excludePhotos?: any[]
  /**
   * 单次最多可选数量,超出在"完成"时拦截 + message.warning,不关闭 picker。
   * 不传 = 不限制(batch-editor 原行为);aiClerk 场景传 10,避免 context 过大
   */
  maxCount?: number
  /**
   * 放宽过滤规则:true 时不强制 hasWatermark / 防伪码 / baseID!=21,
   * 适合 aiClerk 这种"任意团队照片都可选"的场景
   */
  relaxFilter?: boolean
}

const AddBatchEditPhotos: React.FC<AddBatchEditPhotosProps> = ({
  inModal = false,
  onClose,
  currentPhoto,
  headerTitle,
  onConfirm,
  excludePhotos,
  maxCount,
  relaxFilter = false
}) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const currentWatermarkKey = useMemo(() => {
    if (!currentPhoto) return null
    const baseID =
      (currentPhoto as any).watermarkBaseID ||
      (currentPhoto.watermarkID ? currentPhoto.watermarkID.split('+')[0] : '')
    const groupID =
      (currentPhoto as any).groupWatermarkID ||
      (currentPhoto.watermarkID && currentPhoto.watermarkID.includes('+')
        ? currentPhoto.watermarkID.split('+')[1]
        : '')

    if (!baseID && !groupID && currentPhoto.watermarkID) {
      return currentPhoto.watermarkID
    }
    if (!baseID && !groupID) return null
    if (baseID && groupID) return `${baseID}+${groupID}`
    return baseID || currentPhoto.watermarkID || null
  }, [currentPhoto])

  // Redux selectors
  const filter = useAppSelector((state) => state.photo.filter)
  const queryFilter = useAppSelector((state) => state.photo.queryFilter)
  const photos = useAppSelector((state) => state.photo.photos)
  const checkAll = useAppSelector((state) => state.photo.checkAll)
  const checkedPhotos = useAppSelector((state) => state.photo.checkedPhotos)
  const unCheckedPhotos = useAppSelector((state) => state.photo.unCheckedPhotos)
  const groupId = useAppSelector((state) => state.group.groupId)

  const sameWatermarkOnly = useMemo(() => {
    if (!currentWatermarkKey) return false
    const watermarks = filter.watermarks
    if (!watermarks || watermarks.length !== 1) return false
    return watermarks[0] === currentWatermarkKey
  }, [filter, currentWatermarkKey])

  const prevWatermarksRef = useRef<any>(null)

  // 使用埋点 hook
  const { trackAddPhotos } = useAddPhotosTrack()

  const [popoverVisible, setPopoverVisible] = useState(false)
  const backButtonRef = React.useRef<HTMLSpanElement>(null)

  // 提示关闭状态（从缓存读取）
  const [showFilterTip, setShowFilterTip] = useState(true)
  const FILTER_TIP_CLOSED_KEY = 'add-batch-filter-tip-closed'

  useEffect(() => {
    const isClosed = localStorage.getItem(FILTER_TIP_CLOSED_KEY) === 'true'
    setShowFilterTip(!isClosed)
  }, [])

  const handleCloseTip = () => {
    localStorage.setItem(FILTER_TIP_CLOSED_KEY, 'true')
    setShowFilterTip(false)
  }

  // 已选照片来源:aiClerk 调用方通过 excludePhotos prop 传 (走 callback 流)
  // batch-editor 老链路 (不传 excludePhotos) 维持 redux photoPicker 读取
  const photoPickerPhotos = useAppSelector((state) => {
    if (state.photoPicker.from === 'batch-editor') {
      return state.photoPicker.photos
    }
    return []
  })
  const selectedPhotosFromPicker = useMemo(
    () => (excludePhotos ? excludePhotos : photoPickerPhotos),
    [excludePhotos, photoPickerPhotos]
  )

  const handleFetchPhotos = useCallback(
    (isAscSort: boolean, overrideFilter?: any) => {
      dispatch({ type: actionTypes.CLEAR_CHECKED_PHOTO })
      dispatch({ type: actionTypes.CLEAR_UNCHECKED_PHOTO })
      dispatch({ type: actionTypes.SET_CHECK_ALL, data: false })
      dispatch(resetPhotosByLocation())
      dispatch(resetPhotosByPrecise())

      const finalIsAscSort = isAscSort || false

      dispatch(setContentIsAscSort(finalIsAscSort))
      dispatch(setPreciseIsAscSort(finalIsAscSort))

      dispatch(setLoading(true))

      const promises: Promise<any>[] = []

      promises[0] = dispatch(
        fetchPhotos(
          buildFetchPhotosParamsWithOptions({
            queryFilter: overrideFilter || (queryFilter ? queryFilter : filter),
            groupID: groupId,
            isAscSort: finalIsAscSort,
            lastPageCond: ''
          })
        )
      )
        .then((result: any) => {
          if (result.payload && result.payload.expandInfo) {
            dispatch({
              type: actionTypes.SET_PHOTOS_EXPAND_INFO,
              data: result.payload.expandInfo
            })
          }
          return result
        })
        .catch(capturePromiseException)

      Promise.all(promises).then(() => {
        dispatch(setLoading(false))
      })
    },
    [dispatch, filter, groupId, queryFilter]
  )

  const triggerFilter = useCallback(
    (newFilter: any) => {
      dispatch(setLastPageCond(''))

      const finalFilter = {
        ...newFilter,
        suggestedSearchWords:
          newFilter.searchKeywords.length !== 1
            ? []
            : newFilter.suggestedSearchWords,
        useSegment: true
      }

      dispatch(setFilter(finalFilter))
      dispatch(setContentIsAscSort(false))
      dispatch(setPreciseIsAscSort(false))

      dispatch(setQueryFilter(finalFilter)).then(() => {
        handleFetchPhotos(false, finalFilter)
      })
    },
    [dispatch, handleFetchPhotos]
  )

  const handleToggleSameWatermark = useCallback(
    (checked: boolean) => {
      if (!currentWatermarkKey) {
        return
      }

      if (checked) {
        prevWatermarksRef.current = filter.watermarks
        const newFilter = { ...filter, watermarks: [currentWatermarkKey] }
        triggerFilter(newFilter)
      } else {
        const restoreList = prevWatermarksRef.current || []
        const newFilter = { ...filter, watermarks: restoreList }
        triggerFilter(newFilter)
        prevWatermarksRef.current = null
      }
    },
    [currentWatermarkKey, filter, triggerFilter]
  )

  // 检查照片是否有防伪码（用于 hasSelectedPhotos 计算）
  const hasAntiFakeCodeForMemo = useCallback((photo: any): boolean => {
    try {
      const extension = photo.extension
      if (!extension) {
        return false
      }
      const extensionObj =
        typeof extension === 'string' ? JSON.parse(extension) : extension
      const antiFakeCode = extensionObj?.antiFakeCode
      return antiFakeCode && antiFakeCode.trim().length > 0
    } catch (error) {
      console.error('解析 extension 失败:', error)
      return false
    }
  }, [])

  // 单照片是否符合"可被选"的硬条件
  const photoMatchesPickRules = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    (p: any, selectedSmallURLs: Set<unknown>): boolean => {
      if (relaxFilter) return true
      const hasWatermark = p.hasWatermark === true
      const isImage = p.mediaType === 0
      const notSelected = !selectedSmallURLs.has(p.smallURL)
      const photoWatermarkBaseID = p.watermarkBaseID
      const isNotWatermarkBaseID21 = photoWatermarkBaseID !== '21'
      const hasAntiFake = hasAntiFakeCodeForMemo(p)
      return (
        hasWatermark &&
        isImage &&
        notSelected &&
        isNotWatermarkBaseID21 &&
        hasAntiFake
      )
    },
    [relaxFilter, hasAntiFakeCodeForMemo]
  )

  // 计算是否有选中的照片（当前页面勾选但未添加的照片）
  const hasSelectedPhotos = useMemo(() => {
    const selectedSmallURLs = new Set(
      selectedPhotosFromPicker.map((p: any) => p.smallURL)
    )
    const filteredPhotos = photos.filter((p: any) =>
      photoMatchesPickRules(p, selectedSmallURLs)
    )
    if (checkAll) {
      const unCheckedSeqSet = new Set(Object.keys(unCheckedPhotos).map(Number))
      return filteredPhotos.some((p: any) => !unCheckedSeqSet.has(p.seq))
    }
    const checkedSeqSet = new Set(Object.keys(checkedPhotos).map(Number))
    return filteredPhotos.some((p: any) => checkedSeqSet.has(p.seq))
  }, [
    photos,
    checkAll,
    checkedPhotos,
    unCheckedPhotos,
    selectedPhotosFromPicker,
    photoMatchesPickRules
  ])

  // Initialization
  useEffect(() => {
    let initFilter = { ...filter }

    if (currentWatermarkKey) {
      const currentWatermarks = filter.watermarks || []
      const alreadySame =
        currentWatermarks.length === 1 &&
        currentWatermarks[0] === currentWatermarkKey

      if (!alreadySame) {
        prevWatermarksRef.current = currentWatermarks
      }

      initFilter = { ...filter, watermarks: [currentWatermarkKey] }
      dispatch(setFilter(initFilter))
    }

    dispatch({ type: actionTypes.CLEAR_CHECKED_PHOTO })
    dispatch({ type: actionTypes.CLEAR_UNCHECKED_PHOTO })
    dispatch({ type: actionTypes.SET_CHECK_ALL, data: false })

    // 埋点：进入页面
    const startTime = initFilter.times[0]
    const endTime = initFilter.times[1]
    const locations = initFilter.locations
    const watermarks = initFilter.watermarks
    const searchKeywords = initFilter.searchKeywords

    const watermarkList = watermarks && watermarks.length > 0 ? watermarks : []
    const searchWordValue =
      searchKeywords && searchKeywords.length > 0 ? searchKeywords[0] || '' : ''

    trackAddPhotos({
      actionType: 'enterPage',
      start_date: formatDate(startTime ? startTime : null),
      end_date: formatDate(endTime ? endTime : null),
      locationName:
        locations && locations.length > 0
          ? locations[0]?.name || '不限'
          : '不限',
      show_watermark_list: watermarkList,
      searchWord: searchWordValue
    })

    const currentGroupId = localStorage.getItem('group_id')

    if (currentGroupId) {
      dispatch(setQueryFilter(initFilter))
      dispatch(
        fetchPhotos(buildFetchPhotosParams(initFilter, currentGroupId, ''))
      ).catch(capturePromiseException)
    } else {
      navigate('/choose_group', { replace: true })
    }

    // Cleanup
    return () => {
      dispatch(resetFilter())
      dispatch({ type: actionTypes.SET_PHOTOS, data: [] })
      dispatch({ type: actionTypes.SET_PHOTOS_NUM, data: 0 })
      dispatch(resetPhotosByLocation())
      dispatch(resetPhotosByPrecise())
      dispatch(resetAllPhotosSlice())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleBack = () => {
    if (!onConfirm && hasSelectedPhotos) {
      setPopoverVisible(true)
      return
    }
    doBack()
  }

  const doBack = () => {
    const searchKeywords = filter.searchKeywords
    trackAddPhotos({
      actionType: 'back',
      searchWord:
        searchKeywords && searchKeywords.length > 0
          ? searchKeywords[0] || ''
          : ''
    })

    if (inModal) {
      if (onClose) {
        onClose()
        return
      }
      navigate('/watermark/batchEditor')
      return
    }
    navigate('/watermark/batchEditor')
  }

  const handleCancelBack = () => {
    setPopoverVisible(false)
  }

  const handleConfirmBack = () => {
    setPopoverVisible(false)
    doBack()
  }

  // Popover 内容
  const popoverContent = (
    <div className="back-confirm-popover">
      <div className="back-confirm-popover__content">
        <ExclamationCircleOutlined className="back-confirm-popover__icon" />
        <div className="back-confirm-popover__text">
          <div>确认返回吗,照片还未添加</div>
          <div>如需添加请点击右上角&quot;完成&quot;</div>
        </div>
      </div>
      <div className="back-confirm-popover__buttons">
        <Button
          onClick={handleCancelBack}
          className="back-confirm-popover__cancel"
        >
          取消
        </Button>
        <Button
          type="primary"
          onClick={handleConfirmBack}
          className="back-confirm-popover__confirm"
        >
          返回
        </Button>
      </div>
    </div>
  )

  const handleConfirm = () => {
    const selectedSmallURLs = new Set(
      selectedPhotosFromPicker.map((p: any) => p.smallURL)
    )

    const filteredPhotos = photos.filter((p: any) =>
      photoMatchesPickRules(p, selectedSmallURLs)
    )

    let selectedPhotosData: any[] = []

    if (checkAll) {
      const unCheckedSeqSet = new Set(Object.keys(unCheckedPhotos).map(Number))
      selectedPhotosData = filteredPhotos.filter(
        (p: any) => !unCheckedSeqSet.has(p.seq)
      )
    } else {
      const checkedSeqSet = new Set(Object.keys(checkedPhotos).map(Number))
      selectedPhotosData = filteredPhotos.filter((p: any) =>
        checkedSeqSet.has(p.seq)
      )
    }

    if (selectedPhotosData.length === 0) {
      // eslint-disable-next-line no-console
      console.warn('[TeamPhotoPicker] handleConfirm: selectedPhotosData=0', {
        photosSize: photos.length,
        filteredSize: filteredPhotos.length,
        checkAll,
        checkedPhotosKeys: Object.keys(checkedPhotos).map(Number),
        unCheckedPhotosKeys: Object.keys(unCheckedPhotos).map(Number),
        excludePhotosCount: excludePhotos?.length || 0,
        relaxFilter
      })
      message.warning('请至少选择一张照片')
      return
    }

    if (
      typeof maxCount === 'number' &&
      maxCount > 0 &&
      selectedPhotosData.length > maxCount
    ) {
      message.warning(`最多可添加 ${maxCount} 张图片`)
      return
    }

    // 埋点：点击完成
    const searchKeywords = filter.searchKeywords
    trackAddPhotos({
      actionType: 'complete',
      photoNum: selectedPhotosData.length,
      searchWord:
        searchKeywords && searchKeywords.length > 0
          ? searchKeywords[0] || ''
          : ''
    })

    // aiClerk 流:走 callback,不动 photoPicker redux / 不 push 路由
    if (onConfirm) {
      onConfirm(selectedPhotosData)
      if (inModal && onClose) onClose()
      return
    }

    if (selectedPhotosFromPicker.length > 0) {
      const existingSeqSet = new Set(
        selectedPhotosFromPicker.map((p: any) => p.seq)
      )
      const filteredSelectedPhotos = selectedPhotosData.filter(
        (p: any) => !existingSeqSet.has(p.seq)
      )

      if (filteredSelectedPhotos.length === 0) {
        message.warning('已存在相同的照片，请重新选择')
        return
      }

      if (filteredSelectedPhotos.length !== selectedPhotosData.length) {
        message.warning(
          `已移除 ${
            selectedPhotosData.length - filteredSelectedPhotos.length
          } 张重复照片`
        )
      }

      dispatch(appendPhotoPickerPhotos(filteredSelectedPhotos))
    } else {
      dispatch(setPhotoPickerPhotos(selectedPhotosData, 'batch-editor'))
    }
    if (inModal) {
      if (onClose) {
        onClose()
        return
      }
      navigate('/watermark/batchEditor')
      return
    }
    navigate('/watermark/batchEditor')
  }

  return (
    <TeamPhotoPickerContext.Provider
      value={{
        excludePhotos: excludePhotos || [],
        relaxFilter,
        maxCount
      }}
    >
      <Layout
        className={`Photo add-batch-photos-wrapper${
          inModal ? ' is-modal' : ''
        }`}
      >
        <PageHeader
          ghost={false}
          title={
            <div className="add-batch-photos-header-title">
              <Popover
                content={popoverContent}
                open={popoverVisible}
                onOpenChange={(open) => {
                  setPopoverVisible(open)
                }}
                placement="bottomLeft"
                trigger="click"
                classNames={{ root: 'back-confirm-popover-wrapper' }}
                getPopupContainer={(triggerNode) =>
                  triggerNode.parentElement || document.body
                }
              >
                <span
                  ref={backButtonRef}
                  className="add-batch-photos-back-btn"
                  onClick={handleBack}
                >
                  <LeftOutlined />
                </span>
              </Popover>
              <span className="add-batch-photos-title-text">
                {headerTitle || '添加批量编辑照片'}
              </span>
            </div>
          }
          className="add-batch-photos-header"
          extra={[
            <Button key="confirm" type="primary" onClick={handleConfirm}>
              完成
            </Button>
          ]}
        />

        <Content className="content add-batch-photos-content">
          <Filter sameWatermarkOnly={sameWatermarkOnly} />
          {/* 自动过滤提示 — relaxFilter=true(aiClerk 等全量选图场景)没有任何客户端过滤,
            提示条不展示;只有 batch-editor 原链路才显示这条 */}
          {!relaxFilter && showFilterTip && (
            <div className="add-batch-filter-tip">
              <InfoCircleOutlined className="add-batch-filter-tip__icon" />
              <span className="add-batch-filter-tip__text">
                已自动过滤：视频、无水印照片、已选择照片、考勤水印照片等类型
              </span>
              <CloseOutlined
                className="add-batch-filter-tip__close"
                onClick={handleCloseTip}
              />
            </div>
          )}
          <HeaderExtra
            sameWatermarkOnly={sameWatermarkOnly}
            currentWatermarkKey={currentWatermarkKey}
            onToggleSameWatermark={handleToggleSameWatermark}
          />

          <div className="add-batch-photos-list-section">
            <PhotosList inModal={inModal} />
          </div>
        </Content>
      </Layout>
    </TeamPhotoPickerContext.Provider>
  )
}

export default AddBatchEditPhotos
